import { useEffect, useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  isSessionValid,
  getTimeUntilSessionExpires,
  validateAndRefreshSession,
  getSessionInfo,
} from "@/utils/sessionUtils";
import { performComprehensiveLogout } from "@/utils/cacheUtils";

interface SessionMonitorOptions {
  checkInterval?: number; // milliseconds
  warningThreshold?: number; // milliseconds before expiry to show warning
  autoRefreshThreshold?: number; // milliseconds before expiry to auto-refresh
  enableWarnings?: boolean;
  enableAutoRefresh?: boolean;
}

export const useSessionMonitor = (options: SessionMonitorOptions = {}) => {
  const {
    checkInterval = 60000, // 1 minute
    warningThreshold = 5 * 60 * 1000, // 5 minutes
    autoRefreshThreshold = 10 * 60 * 1000, // 10 minutes
    enableWarnings = true,
    enableAutoRefresh = true,
  } = options;

  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const { toast } = useToast();

  const checkSession = useCallback(async () => {
    try {
      const info = await getSessionInfo();
      setSessionInfo(info);
      setLastCheck(new Date());

      if (!info.hasSession) {
        return;
      }

      // Handle expired session
      if (info.isExpired) {
        toast({
          title: "Session expired",
          description: "Your session has expired. Please log in again.",
          variant: "destructive",
        });
        await performComprehensiveLogout();
        window.location.href = "/auth";
        return;
      }

      // Handle session expiring soon
      if (enableWarnings && info.expiresSoon && info.timeUntilExpiration) {
        const minutesLeft = Math.ceil(info.timeUntilExpiration / (60 * 1000));
        toast({
          title: "Session expiring soon",
          description: `Your session will expire in ${minutesLeft} minute${
            minutesLeft !== 1 ? "s" : ""
          }. Please save your work.`,
          duration: 10000,
        });
      }

      // Auto-refresh session if it's about to expire
      if (
        enableAutoRefresh &&
        info.timeUntilExpiration &&
        info.timeUntilExpiration < autoRefreshThreshold
      ) {
        const refreshed = await validateAndRefreshSession();
        if (refreshed) {
          // Update session info after refresh
          const newInfo = await getSessionInfo();
          setSessionInfo(newInfo);
        }
      }
    } catch (error) {
      console.error("Error checking session:", error);
    }
  }, [
    warningThreshold,
    autoRefreshThreshold,
    enableWarnings,
    enableAutoRefresh,
    toast,
  ]);

  const startMonitoring = useCallback(() => {
    setIsMonitoring(true);
    checkSession(); // Initial check
  }, [checkSession]);

  const stopMonitoring = useCallback(() => {
    setIsMonitoring(false);
  }, []);

  // Handle cross-tab logout events
  useEffect(() => {
    const handleComprehensiveLogout = () => {
      setSessionInfo({ hasSession: false });
      setIsMonitoring(false);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "SUPABASE_SESSION" && !event.newValue) {
        setSessionInfo({ hasSession: false });
        setIsMonitoring(false);
      }
    };

    window.addEventListener("comprehensive-logout", handleComprehensiveLogout);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(
        "comprehensive-logout",
        handleComprehensiveLogout
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Set up monitoring interval
  useEffect(() => {
    if (!isMonitoring) return;

    const interval = setInterval(checkSession, checkInterval);

    return () => {
      clearInterval(interval);
    };
  }, [isMonitoring, checkInterval, checkSession]);

  // Auto-start monitoring when component mounts
  useEffect(() => {
    startMonitoring();
  }, [startMonitoring]);

  return {
    sessionInfo,
    isMonitoring,
    lastCheck,
    checkSession,
    startMonitoring,
    stopMonitoring,
    isValid: sessionInfo?.hasSession && !sessionInfo?.isExpired,
    expiresSoon: sessionInfo?.expiresSoon,
    timeUntilExpires: sessionInfo?.timeUntilExpiration,
  };
};
